import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Wrench, ArrowRight } from "@phosphor-icons/react";
import { SocialSignIn } from "@/components/SocialSignIn";

import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COUNTRIES } from "@/lib/countries";

const AGENT_COVERS = [
  ["cargo", "Cargo in transit"],
  ["cart", "Local cart carriers"],
  ["fleet", "Fleet & vehicles"],
  ["business", "Facilities & equipment"],
  ["liability", "Authority liability"],
];

const LENDER_FINANCE = [
  ["working_capital", "Working capital"],
  ["term_loan", "Term loan"],
  ["vehicle_fleet", "Vehicle & fleet"],
  ["invoice_finance", "Invoice finance"],
  ["trade_finance", "Trade finance"],
  ["leasing", "Leasing"],
  ["po_finance", "PO finance"],
  ["overdraft", "Overdraft"],
  ["islamic", "Islamic finance"],
  ["supplier_credit", "Supplier credit"],
  ["p2p", "Peer-to-peer"],
  ["equity", "Equity"],
  ["grant", "Grants"],
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "member", company: "", invite_code: "",
    agency_country: "US", agency_city: "", agency_covers_whole_country: true,
    agency_international: false, agency_cover_types: ["cargo", "cart"],
    lender_kind: "bank", lender_sector: "private",
    lender_finance_types: ["working_capital", "term_loan"],
    accept_terms: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await register(form);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="grid-lines flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4 py-12">
      <div className="w-full max-w-lg border border-border bg-white p-8 sm:p-10">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center bg-[#002FA7]">
            <Wrench size={20} weight="bold" className="text-white" />
          </div>
          <div className="font-display text-sm font-extrabold tracking-tight">UNITY &amp; UTILITY</div>
        </div>

        <div className="overline mb-2 text-[#002FA7]">Join the floor</div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Create account.
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="overline text-slate-500">Full name</Label>
              <Input
                data-testid="register-name-input"
                value={form.name}
                onChange={set("name")}
                required
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="overline text-slate-500">
                {form.role === "agent" ? "Agency name" : form.role === "lender" ? "Institution name" : "Company / plant"}
              </Label>
              <Input
                data-testid="register-company-input"
                value={form.company}
                onChange={set("company")}
                required={form.role === "agent" || form.role === "lender"}
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
          </div>

          <div>
            <Label className="overline text-slate-500">Invite code (optional)</Label>
            <Input
              data-testid="register-invite-input"
              value={form.invite_code}
              onChange={set("invite_code")}
              placeholder="Sets your role automatically"
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>

          <div>
            <Label className="overline text-slate-500">Email</Label>
            <Input
              data-testid="register-email-input"
              type="email"
              value={form.email}
              onChange={set("email")}
              required
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="overline text-slate-500">Password</Label>
              <Input
                data-testid="register-password-input"
                type="password"
                value={form.password}
                onChange={set("password")}
                required
                minLength={6}
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div>
              <Label className="overline text-slate-500">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="register-role-select" className="mt-1.5 rounded-none border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member (floor)</SelectItem>
                  <SelectItem value="carrier">Carrier / driver</SelectItem>
                  <SelectItem value="vendor">Vendor / Seller</SelectItem>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="agent">Insurance agent</SelectItem>
                  <SelectItem value="lender">Bank / lender officer</SelectItem>
                  <SelectItem value="scheme_officer">Government scheme officer</SelectItem>
                  <SelectItem value="legal_advisor">Legal advocate / advisor</SelectItem>
                  <SelectItem value="service_provider">Professional service provider</SelectItem>
                </SelectContent>
              </Select>
              {form.role === "carrier" && (
                <p className="mt-1.5 text-xs text-slate-500" data-testid="carrier-signup-hint">
                  You'll be able to publish your own pick-up services, see the job board and get paid to your bank or UPI.
                </p>
              )}
              {form.role === "agent" && (
                <p className="mt-1.5 text-xs text-slate-500" data-testid="agent-signup-hint">
                  You'll get an agent desk showing risks in your patch. An admin verifies your agency before you can quote.
                </p>
              )}
              {form.role === "lender" && (
                <p className="mt-1.5 text-xs text-slate-500" data-testid="lender-signup-hint">
                  You'll get a finance desk with applications from your patch. An admin verifies your institution before you can offer terms.
                </p>
              )}
              {form.role === "scheme_officer" && (
                <p className="mt-1.5 text-xs text-slate-500" data-testid="scheme-signup-hint">
                  You'll be able to publish government scheme policies and decide the claims made against them.
                </p>
              )}
              {form.role === "legal_advisor" && (
                <p className="mt-1.5 text-xs text-slate-500" data-testid="legal-signup-hint">
                  You'll publish your practice — countries admitted, licence and practice areas. An admin verifies it before you can take matters on.
                </p>
              )}
            </div>
          </div>

          {form.role === "agent" && (
            <div className="space-y-4 border border-[#C7D2FE] bg-[#EEF2FF] p-4" data-testid="agent-signup-fields">
              <div className="overline text-[#002FA7]">Your patch</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="overline text-slate-500">Country</Label>
                  <Select
                    value={form.agency_country}
                    onValueChange={(v) => setForm({ ...form, agency_country: v })}
                  >
                    <SelectTrigger data-testid="agent-country-select" className="mt-1.5 rounded-none border-slate-300 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline text-slate-500">City / region</Label>
                  <Input
                    data-testid="agent-city-input"
                    value={form.agency_city}
                    onChange={set("agency_city")}
                    placeholder="Where you are based"
                    className="mt-1.5 rounded-none border-slate-300 bg-white"
                  />
                </div>
              </div>
              <div>
                <Label className="overline text-slate-500">Cover you write</Label>
                <div className="mt-1.5 flex flex-wrap gap-2" data-testid="agent-cover-types">
                  {AGENT_COVERS.map(([k, label]) => (
                    <button
                      type="button"
                      key={k}
                      data-testid={`agent-cover-${k}`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          agency_cover_types: f.agency_cover_types.includes(k)
                            ? f.agency_cover_types.filter((c) => c !== k)
                            : [...f.agency_cover_types, k],
                        }))
                      }
                      className={`border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                        form.agency_cover_types.includes(k)
                          ? "border-[#002FA7] bg-[#002FA7] text-white"
                          : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  data-testid="agent-whole-country-checkbox"
                  checked={form.agency_covers_whole_country}
                  onChange={(e) => setForm({ ...form, agency_covers_whole_country: e.target.checked })}
                  className="h-4 w-4 accent-[#002FA7]"
                />
                I cover the whole country, not just my city
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  data-testid="agent-international-checkbox"
                  checked={form.agency_international}
                  onChange={(e) => setForm({ ...form, agency_international: e.target.checked })}
                  className="h-4 w-4 accent-[#002FA7]"
                />
                I also write international / cross-border risks
              </label>
            </div>
          )}

          {error && (
            <div
              data-testid="register-error"
              className="border border-[#F6B9B5] bg-[#FFECEB] px-3 py-2 text-sm text-[#B3241B]"
            >
              {error}
            </div>
          )}

          <label className="flex cursor-pointer items-start gap-2 text-xs text-slate-600" data-testid="accept-terms-label">
            <input
              type="checkbox"
              data-testid="accept-terms-checkbox"
              checked={form.accept_terms}
              onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
              required
              className="mt-0.5 h-4 w-4 accent-[#002FA7]"
            />
            <span>
              I agree to the{" "}
              <a href="/legal/terms" target="_blank" rel="noreferrer" className="font-semibold text-[#002FA7] underline">terms of use</a>,{" "}
              <a href="/legal/privacy" target="_blank" rel="noreferrer" className="font-semibold text-[#002FA7] underline">privacy policy</a> and{" "}
              <a href="/legal/cookies" target="_blank" rel="noreferrer" className="font-semibold text-[#002FA7] underline">cookie notice</a>.
            </span>
          </label>

          <Button
            data-testid="register-submit-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-none bg-[#002FA7] py-6 text-sm font-semibold uppercase tracking-widest text-white hover:bg-[#00227A]"
          >
            {busy ? "Creating…" : "Create account"}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </form>

        <SocialSignIn label="Or sign up with" />


        <p className="mt-6 text-sm text-slate-500">
          Already a member?{" "}
          <Link data-testid="go-to-login-link" to="/login" className="font-semibold text-[#002FA7] hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
